
import type { RefineOffer, RefineResult } from "../net/messages";
import type { SfxPlayer } from "../audio/sfx";
import { itemArtNode } from "./itemart";
import { elem, styleInstaller } from "./chrome";

export type { RefineOffer, RefineResult };

export interface RefineActions {
  /** Try it. The panel closes itself and waits for the outcome frame. */
  onRefine?: (slot: number) => void;
  /** Walk away. RefineDialogNew sends the slot-255 cancel sentinel. */
  onCancel?: () => void;
  /** `make_soket` on success, `jaeryun_fail` on failure -- see `settle`.
   *  Optional so a test fixture built without one stays silent. */
  sfx?: SfxPlayer;
}

const STYLE_ID = "refine-style";

const STYLE = `
.refine-panel {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 60;
  width: min(320px, 84vw);
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(12, 14, 18, 0.94);
  border: 1px solid rgba(255, 236, 170, 0.22);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  font: 13px/1.5 system-ui, sans-serif;
  color: #d8dee6;
}
.refine-title {
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9b9484;
  margin-bottom: 8px;
}
.refine-becomes { font-size: 1rem; color: #ffd27a; margin-bottom: 10px; }
.refine-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 8px;
  min-height: 34px;
}
.refine-slot {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 32px;
  min-height: 34px;
  border-radius: 4px;
  border: 1px solid rgba(255, 236, 170, 0.22);
  background: rgba(255, 255, 255, 0.03);
  flex: 0 0 auto;
}
.refine-slot .item-icon {
  width: 32px;
  height: auto;
  margin: 0;
}
.refine-preview-arrow { color: #9b9484; }
.refine-line { display: flex; justify-content: space-between; gap: 12px; align-items: center; }
.refine-line + .refine-line { margin-top: 3px; }
.refine-line-short { color: #e0736a; }
.refine-materials { margin: 8px 0 0; padding: 8px 0 0; border-top: 1px solid rgba(255, 255, 255, 0.08); }
.refine-material {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.refine-material:first-child { margin-top: 0; }
.refine-material-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
}
.refine-material-icon .item-icon {
  height: 24px;
  margin: 0;
}
.refine-material .refine-line { flex: 1 1 auto; margin-top: 0; }
.refine-question {
  margin-top: 10px;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(180, 60, 50, 0.18);
  border: 1px solid rgba(224, 115, 106, 0.35);
  color: #f0b8b2;
  font-size: 0.85rem;
}
.refine-buttons { display: flex; gap: 8px; margin-top: 12px; }
.refine-button {
  flex: 1;
  padding: 6px 0;
  border-radius: 6px;
  border: 1px solid rgba(255, 210, 120, 0.4);
  background: rgba(255, 210, 120, 0.12);
  color: #ffd27a;
  font: inherit;
  cursor: pointer;
}
.refine-button:hover:not(:disabled) { background: rgba(255, 210, 120, 0.22); }
.refine-button:disabled { opacity: 0.45; cursor: default; }
.refine-question .refine-buttons { margin-top: 8px; }
.refine-question-button {
  flex: 1;
  padding: 4px 0;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: transparent;
  color: #f0b8b2;
  font: inherit;
  cursor: pointer;
}
.refine-button-cancel { border-color: rgba(255, 255, 255, 0.18); background: transparent; color: #b8bec6; }
.refine-result { margin-top: 10px; font-size: 0.9rem; }
.refine-result-good { color: #8fd18a; }
.refine-result-bad { color: #e0736a; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export function hasMaterials(offer: RefineOffer): boolean {
  return offer.materials.every((m) => m.have >= m.need);
}

/** Whether Improve should be clickable. */
export function canAttempt(offer: RefineOffer): boolean {
  return offer.affordable && hasMaterials(offer);
}


export class RefinePanel {
  private readonly root: HTMLElement;
  private readonly preview: HTMLElement;
  private readonly targetSlot: HTMLElement;
  private readonly resultSlot: HTMLElement;
  private readonly becomes: HTMLElement;
  private readonly details: HTMLElement;
  private readonly question: HTMLElement;
  private readonly questionWarning: HTMLElement;
  private readonly improve: HTMLButtonElement;
  private readonly result: HTMLElement;
  private offer: RefineOffer | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: RefineActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "refine-panel");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "false");

    const title = elem(doc, "div", "refine-title");
    title.id = "refine-title";
    title.textContent = "Upgrades"; // REFINE_TTILE (`uiscript/refinedialog.py:44`, `locale_interface.txt:275`)
    this.root.setAttribute("aria-labelledby", title.id);

    this.preview = elem(doc, "div", "refine-preview");
    this.preview.hidden = true;
    this.targetSlot = elem(doc, "div", "refine-slot refine-target");
    this.targetSlot.setAttribute("aria-hidden", "true");
    const arrow = elem(doc, "span", "refine-preview-arrow", "→");
    this.resultSlot = elem(doc, "div", "refine-slot refine-result-slot");
    this.resultSlot.setAttribute("aria-hidden", "true");
    this.preview.append(this.targetSlot, arrow, this.resultSlot);

    this.becomes = elem(doc, "div", "refine-becomes");

    this.details = doc.createElement("div");

    // QuestionDialog2 (`uirefine.py:398-401`, `uiscript/questiondialog2.py:28-81`).
    this.question = elem(doc, "div", "refine-question");
    this.question.hidden = true;
    this.questionWarning = elem(doc, "div", "refine-question-text");
    // REFINE_WARNING2 (`uirefine.py:399`, `locale_game.txt:409`).
    const questionAsk = elem(doc, "div", "refine-question-text", "Do you want to continue?");
    const questionButtons = elem(doc, "div", "refine-buttons");
    const yes = doc.createElement("button");
    yes.type = "button";
    yes.className = "refine-question-button";
    yes.textContent = "Yes"; // YES (`uiscript/questiondialog2.py:64`, `locale_interface.txt:325`)
    yes.addEventListener("click", () => {
      this.question.hidden = true;
      this.attempt();
    });
    const no = doc.createElement("button");
    no.type = "button";
    no.className = "refine-question-button";
    no.textContent = "No"; // NO (`uiscript/questiondialog2.py:81`, `locale_interface.txt:207`)
    no.addEventListener("click", () => {
      this.question.hidden = true;
    });
    questionButtons.append(yes, no);
    this.question.append(this.questionWarning, questionAsk, questionButtons);

    this.result = elem(doc, "div", "refine-result");
    this.result.hidden = true;

    const buttons = elem(doc, "div", "refine-buttons");

    this.improve = doc.createElement("button");
    this.improve.type = "button";
    this.improve.className = "refine-button";
    this.improve.textContent = "OK"; // AcceptButton (`uiscript/refinedialog.py:79`, `locale_interface.txt:208`)
    this.improve.addEventListener("click", () => this.confirm());

    const cancel = doc.createElement("button");
    cancel.type = "button";
    cancel.className = "refine-button refine-button-cancel";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.cancel());

    buttons.append(this.improve, cancel);
    this.root.append(
      title,
      this.preview,
      this.becomes,
      this.details,
      buttons,
      this.question,
      this.result,
    );
    // `RefineDialogNew.OnPressEscapeKey` cancels the pending refinement and
    // consumes Escape (`uirefine.py:418-424`). Keep this on the visible panel,
    // sharing the same close path as the Cancel button so an Escape does not
    // leak into world movement or leave the server-side offer active.
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.cancel();
    });
    container.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** What Improve would send. For tests, and for a keyboard shortcut. */
  get openSlot(): number {
    return this.offer === null ? -1 : this.offer.slot;
  }

  get canImprove(): boolean {
    return !this.improve.disabled;
  }

  /** Draw an offer. Redrawn wholesale, so no row of a previous item survives. */
  update(offer: RefineOffer): void {
    const doc = this.root.ownerDocument;
    this.offer = offer;
    // Keep the server's definition identities attached to the rendered offer,
    // even though the visible label remains localized text. These are the
    // source/result vnums from `TPacketGCRefineInformation`; they are useful to
    // diagnostics and cannot be reconstructed safely from a localized name.
    this.root.dataset.itemVnum = String(offer.item_vnum);
    this.root.dataset.resultVnum = String(offer.result_vnum);
    this.result.hidden = true;
    this.result.textContent = "";

    this.paintPreview(offer.item_vnum, offer.item_kind, offer.result_vnum, offer.result_kind);

    this.becomes.textContent = offer.result_name
      ? `${offer.item_name} → ${offer.result_name}`
      : offer.item_name;

    this.details.replaceChildren();
    this.details.append(
      // REFINE_SUCCESS_PROBALITY, then REFINE_COST (`uirefine.py:298-299`, `locale_game.txt:408,391`).
      line(doc, `Chance for Advancement ${offer.chance}%`, false),
      line(doc, `Costs for Advancement: ${offer.cost} Yang`, !offer.affordable),
    );

    if (offer.materials.length > 0) {
      const list = elem(doc, "div", "refine-materials");
      for (const material of offer.materials) {
        const row = elem(doc, "div", "refine-material");
        const iconCell = elem(doc, "div", "refine-material-icon");
        // The painting, or the class silhouette every other panel draws.
        // `kind` is the server's (`ground.py:item_kind`); this panel used to
        // leave the cell blank because its row carried no such byte.
        iconCell.append(itemArtNode(material.vnum, material.kind, doc));
        row.append(
          iconCell,
          // "%s x %02d" (`uirefine.py:348`).
          line(doc, `${material.name} x ${String(material.need).padStart(2, "0")}`, material.have < material.need),
        );
        list.append(row);
      }
      this.details.append(list);
    }

    this.question.hidden = true;

    this.improve.disabled = !canAttempt(offer);
    this.root.hidden = false;
  }

  settle(outcome: RefineResult): void {
    this.offer = null;
    this.improve.disabled = true;
    // `RefineSuceededMessage`/`RefineFailedMessage` (`game.py:1938,1942` --
    // the original's own misspelling of "socket", not this build's).
    this.actions.sfx?.play(outcome.succeeded ? "make_soket" : "jaeryun_fail");
    this.result.hidden = false;
    this.result.className = `refine-result ${outcome.succeeded ? "refine-result-good" : "refine-result-bad"}`;
    this.result.textContent = outcome.succeeded
      ? `Improved to ${outcome.result_name}.`
      : `${outcome.item_name} was destroyed.`;
  }

  /** Take the window down. Used when the player walks away, dies or logs out. */
  close(): void {
    this.root.hidden = true;
    this.offer = null;
    this.question.hidden = true;
    this.result.hidden = true;
    this.clearPreview();
    this.details.replaceChildren();
  }

  /** Target and result art: the painting, or the class silhouette. 145 vnums
   *  in the shipped refine chain have no TGA in any 2014 pack -- 10 weapons
   *  and 107 pieces of armour among them -- so these two cells were the ones
   *  a blank told a player nothing about. */
  private paintPreview(
    itemVnum: number,
    itemKind: number,
    resultVnum: number,
    resultKind: number,
  ): void {
    const doc = this.root.ownerDocument;
    this.targetSlot.replaceChildren(itemArtNode(itemVnum, itemKind, doc));
    this.resultSlot.replaceChildren(itemArtNode(resultVnum, resultKind, doc));
    this.preview.hidden = false;
  }

  private clearPreview(): void {
    this.targetSlot.replaceChildren();
    this.resultSlot.replaceChildren();
    this.preview.hidden = true;
  }

  /** OK (`OpenQuestionDialog`, `uirefine.py:388-412`): a certain refinement is sent at once; anything else asks first, and only Yes sends. */
  confirm(): void {
    const offer = this.offer;
    if (offer === null || !canAttempt(offer)) return;
    if (offer.chance === 100) {
      this.attempt(); // uirefine.py:390-392
      return;
    }
    // REFINE_DESTROY_WARNING (`uirefine.py:409`, `locale_game.txt:392`), when the frame says a failure destroys.
    this.questionWarning.textContent = offer.will_destroy ? "This Item could be destroyed while upgrading." : "";
    this.question.hidden = false;
  }

  /** Send the attempt. Public so a keyboard shortcut can reach it. */
  attempt(): void {
    const offer = this.offer;
    if (offer === null || !canAttempt(offer)) return;
    // Cleared before the send, so a double-click cannot produce two requests
    // against one item. The server would refuse the second anyway -- the item
    // is gone either way by then -- but a window that lets a player ask twice
    // is a window that looks like it charged them twice.
    this.offer = null;
    this.improve.disabled = true;
    this.actions.onRefine?.(offer.slot);
  }

  cancel(): void {
    this.close();
    this.actions.onCancel?.();
  }
}

function line(doc: Document, text: string, short: boolean): HTMLElement {
  return elem(doc, "div", short ? "refine-line refine-line-short" : "refine-line", text);
}
